import type { AbstractEvent } from '@linktr.ee/event-lib/envelope/Event';
import { MobileClientEventPublisher } from '@linktr.ee/event-lib/mobile';
import { type EventVersions } from '@linktr.ee/event-lib/types/versions';
import { WebClientEventPublisher } from '@linktr.ee/event-lib/web';
import { getOrCreateClientSessionId } from '../clientSessionId';
import { getMobileContextParams, WebViewDeviceStorage } from '../mobileContext';

type EventPublisher = WebClientEventPublisher | MobileClientEventPublisher;
type EventContextWithLanguage = {
  browser?: Record<string, unknown>;
  clientSession?: {
    id?: string;
  };
  language?: unknown;
  locale?: unknown;
};

type AnalyticsContextOverrides = {
  userUuid?: string;
};

const getNonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue || undefined;
};

const normalizeLanguageTag = (language?: string): string | undefined =>
  getNonEmptyString(language)?.replace(/_/g, '-');

const getCurrentLanguageTag = (): string | undefined => {
  const documentLanguage =
    typeof document !== 'undefined'
      ? normalizeLanguageTag(document.documentElement.lang)
      : undefined;

  if (documentLanguage) {
    return documentLanguage;
  }

  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return normalizeLanguageTag(navigator.languages?.[0] || navigator.language);
};

function createPublisher(amplitudeApiKey: string): EventPublisher {
  const mobileContext = getMobileContextParams();

  if (mobileContext?.deviceId) {
    const deviceStorage = new WebViewDeviceStorage(mobileContext);
    return new MobileClientEventPublisher({
      internalEndpoint: import.meta.env.VITE_INGRESS_API_ENDPOINT,
      amplitudeApiKey,
      deviceStorage,
      source: 'descope-universal-login',
    });
  }

  return new WebClientEventPublisher({
    internalEndpoint: import.meta.env.VITE_INGRESS_API_ENDPOINT,
    amplitudeApiKey,
    facebookPixelId: import.meta.env.VITE_FB_PIXEL_ID,
    source: 'descope-universal-login',
  });
}

// Publishers are lazily initialized to ensure window.__MOBILE_APP_CONTEXT__
// (injected by the native FlowView hook) is available before publisher creation.
let _nonGoldenPublisher: EventPublisher | undefined;
let _goldenPublisher: EventPublisher | undefined;

function getNonGoldenPublisher(): EventPublisher {
  if (!_nonGoldenPublisher) {
    _nonGoldenPublisher = createPublisher(
      import.meta.env.VITE_AMPLITUDE_CLIENT_KEY
    );
  }
  return _nonGoldenPublisher;
}

function getGoldenPublisher(): EventPublisher {
  if (!_goldenPublisher) {
    _goldenPublisher = createPublisher(
      import.meta.env.VITE_AMPLITUDE_GOLDEN_CLIENT_KEY
    );
  }
  return _goldenPublisher;
}

const withFallbackAnalyticsContext = <
  E extends keyof EventVersions,
  V extends keyof EventVersions[E],
>(
  event: AbstractEvent<E, V>,
  sessionId: string,
  overrides: AnalyticsContextOverrides = {}
): AbstractEvent<E, V> => {
  const originalBuildContext = event.buildContext.bind(event);

  event.buildContext = (...args: Parameters<typeof event.buildContext>) => {
    const eventWithContext = originalBuildContext(...args);
    const context =
      eventWithContext.context as typeof eventWithContext.context &
        EventContextWithLanguage;
    const languageTag = getCurrentLanguageTag();

    context.clientSession = {
      ...context.clientSession,
      id: context.clientSession?.id || sessionId,
    };

    const userUuid = getNonEmptyString(overrides.userUuid);
    if (userUuid) {
      // `user.uuid` is defined by event-lib's EventEnvelopeV160 context; keep
      // this assignment checked against that package type rather than a local
      // compatibility shim.
      context.user = {
        ...context.user,
        uuid: userUuid,
      };
    }

    if (languageTag) {
      context.language = getNonEmptyString(context.language) || languageTag;
      context.locale = getNonEmptyString(context.locale) || languageTag;
      context.browser = {
        ...context.browser,
        language: getNonEmptyString(context.browser?.language) || languageTag,
      };
    }

    return eventWithContext;
  };

  return event;
};

/**
 * This function is used to publish 'golden' events to Amplitude. Golden events are events that are defined in the golden event schema in the event-lib package.
 *
 * @see publishNonGoldenEvent for non-golden events
 * @param event
 * @returns
 */
export const publishEvent = <
  E extends keyof EventVersions,
  V extends keyof EventVersions[E],
>(
  event: AbstractEvent<E, V>,
  contextOverrides: AnalyticsContextOverrides = {}
): Promise<void> => {
  const sessionId = getOrCreateClientSessionId();
  return getGoldenPublisher().publishEvent(
    withFallbackAnalyticsContext(event, sessionId, contextOverrides),
    { sendToAmplitude: true }
  );
};

/**
 * This function is used to publish non-golden events to Amplitude. This should be used sparingly, as golden events are preferred.
 *
 * @param event
 * @returns
 */
export const publishNonGoldenEvent = <
  E extends keyof EventVersions,
  V extends keyof EventVersions[E],
>(
  event: AbstractEvent<E, V>
) => {
  const sessionId = getOrCreateClientSessionId();

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log('publishNonGoldenEvent', event);
    return;
  }

  getNonGoldenPublisher().publishEvent(
    withFallbackAnalyticsContext(event, sessionId),
    { sendToAmplitude: true }
  );
};
